import React, { useState, useEffect } from 'react';
import {
  Modal,
  Table,
  Spin,
  message,
  Tag,
  Image,
  Typography,
  Button,
  Space,
  Drawer,
  Descriptions,
  List,
  Card
} from 'antd';
import { useTranslation } from 'react-i18next';
import { PlanogramTask, Feedback } from './task';
import { TaskService } from '../../services/taskService';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

const { Title } = Typography;

interface FeedbackModalProps {
  visible: boolean;
  onClose: () => void;
  task: PlanogramTask | null;
}

const FeedbackModal: React.FC<FeedbackModalProps> = ({
  visible,
  onClose,
  task,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [feedbackList, setFeedbackList] = useState<Feedback[]>([]);
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);
  const [drawerVisible, setDrawerVisible] = useState(false);

  // 获取feedback数据
  useEffect(() => {
    if (visible && task) {
      setLoading(true);
      TaskService.getTaskFeedback(task.shop, task.taskCode)
        .then((response) => {
          if (response.success && response.data) {
            setFeedbackList(response.data);
          } else {
            console.warn("获取反馈数据失败:", response.errorMessage);
            message.warning(t("taskManagement.list.feedbackModal.loadFailed"));
            // 即使没有数据也清空列表，避免显示旧数据
            setFeedbackList([]);
          }
        })
        .catch((error) => {
          console.error("获取反馈数据异常:", error);
          message.error(t("taskManagement.list.feedbackModal.loadFailed"));
          // 出错时也清空列表
          setFeedbackList([]);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      // 重置数据
      setFeedbackList([]);
    }
  }, [visible, task]);

  // 查看反馈详情
  const handleViewDetail = (feedback: Feedback) => {
    setSelectedFeedback(feedback);
    setDrawerVisible(true);
  };

  // 关闭详情抽屉
  const closeDrawer = () => {
    setDrawerVisible(false);
    setSelectedFeedback(null);
  };

  // 表格列定义
  const columns: ColumnsType<Feedback> = [
    {
        title: t('taskManagement.list.columns.displayGroupName'),
        dataIndex: 'displayGroupName',
        key: 'displayGroupName',
        width: 120,
    },
    {
        title: t('taskManagement.list.columns.divisionName'),
        dataIndex: 'divisionName',
        key: 'divisionName',
        width: 120,
    },
    {
        title: t('taskManagement.list.feedbackModal.columns.itemCode'),
        dataIndex: 'itemCode',
        key: 'itemCode',
        width: 120,
    },
    {
        title: t('taskManagement.list.feedbackModal.columns.itemName'),
        dataIndex: 'itemName',
        key: 'itemName',
        width: 150,
        render: (_, record) => (
        <span>{record.itemDTO?.name || '-'}</span>
        ),
    },
    {
        title: t('taskManagement.list.columns.seqOnLayer'),
        dataIndex: 'seqOnLayer',
        key: 'seqOnLayer',
        width: 120,
    },
    {
        title: t('taskManagement.list.columns.fromLayerCode'),
        dataIndex: 'fromLayerCode',
        key: 'fromLayerCode',
        width: 150,
    },
    {
        title: t('taskManagement.list.columns.fromLayerNumber'),
        dataIndex: 'fromLayerNumber',
        key: 'fromLayerNumber',
        width: 150,
    },
    {
        title: t('taskManagement.list.columns.action'),
        dataIndex: 'action',
        key: 'action',
        width: 100,
        render: (_, record) => {
        if (record.action) {
            switch (record.action) {
            case 'IN':
                return <span>{t('taskManagement.details.tabs.moveIn')}</span>;
            case 'OUT':
                return <span>{t('taskManagement.details.tabs.moveOut')}</span>;
            case 'REARRANGE':
                return <span>{t('taskManagement.details.tabs.rearrange')}</span>;
            case 'NEW_ITEM':
                return <span>{t('taskManagement.details.tabs.newItem')}</span>;
            default:
                return <span>{record.action}</span>;
            }
        }
        return <span>{record.action || '-'}</span>;
        },
    },
    {
        title: t('taskManagement.list.columns.planNum'),
        dataIndex: 'planNum',
        key: 'planNum',
        width: 100,
    },
    {
        title: t('taskManagement.list.columns.plan4x'),
        dataIndex: 'plan4x',
        key: 'plan4x',
        width: 100,
    },
    {
        title: t('taskManagement.list.columns.plan4y'),
        dataIndex: 'plan4y',
        key: 'plan4y',
        width: 100,
    },
    {
        title: t('taskManagement.list.columns.plan4z'),
        dataIndex: 'plan4z',
        key: 'plan4z',
        width: 100,
    },
    {
        title: t('taskManagement.list.columns.actualNum'),
        dataIndex: 'actualNum',
        key: 'actualNum',
        width: 100,
    },
    {
        title: t('taskManagement.list.columns.actual4x'),
        dataIndex: 'actual4x',
        key: 'actual4x',
        width: 100,
    },
    {
        title: t('taskManagement.list.columns.actual4y'),
        dataIndex: 'actual4y',
        key: 'actual4y',
        width: 100,
    },
    {
        title: t('taskManagement.list.columns.actual4z'),
        dataIndex: 'actual4z',
        key: 'actual4z',
        width: 100,
    },
    {
        title: t('taskManagement.list.feedbackModal.columns.worker'),
        dataIndex: 'workerName',
        key: 'workerName',
        width: 120,
        render: (_, record) => (
        <span>{record.workerName} ({record.workerAccount})</span>
        ),
    },
    {
        title: t('taskManagement.list.feedbackModal.columns.reason'),
        dataIndex: 'reason',
        key: 'reason',
        width: 200,
        render: (reason: string) => (
        <>{t('taskManagement.list.feedbackModal.reason.' + reason) || reason || '-'}</>
        ),
    },
    {
        title: t('taskManagement.list.feedbackModal.columns.fulfillType'),
        dataIndex: 'fulfillType',
        key: 'fulfillType',
        width: 120,
        render: (fulfillType: string) => (
        <Tag color={fulfillType === 'Complete' ? 'green' : 'orange'}>
            {fulfillType === 'cantFulfill' 
            ? t('taskManagement.list.feedbackModal.cantFulfill')
            : t('taskManagement.list.feedbackModal.fulftypePartial')}
        </Tag>
        ),
    },
    {
      title: t('taskManagement.list.columns.gmtCreate'),
      dataIndex: 'gmtCreate',
      key: 'gmtCreate',
      width: 180,
      render: (timestamp: number) => formatTime(timestamp),
    },
    {
      title: t('taskManagement.list.columns.finishTime'),
      dataIndex: 'finishTime',
      key: 'finishTime',
      width: 180,
      render: (timestamp: number) => formatTime(timestamp),
    },
    {
      title: t('taskManagement.list.columns.rejectTime'),
      dataIndex: 'rejectTime',
      key: 'rejectTime',
      width: 180,
      render: (timestamp: number) => formatTime(timestamp),
    },
    // {
    //   title: t('taskManagement.list.feedbackModal.columns.images'),
    //   dataIndex: 'feedbackImagesList',
    //   key: 'feedbackImagesList',
    //   width: 150,
    //   render: (feedbackImagesList: string[]) => (
    //     <span>{feedbackImagesList ? feedbackImagesList.length : 0} {t('taskManagement.list.feedbackModal.images')}</span>
    //   ),
    // },
    // {
    //   title: t('common.actions'),
    //   key: 'actions',
    //   width: 100,
    //   render: (_, record) => (
    //     <Button type="link" onClick={() => handleViewDetail(record)}>
    //       {t('common.view')}
    //     </Button>
    //   ),
    // },
  ];

  const formatTime = (timestamp: number) => {
    return timestamp ? dayjs(timestamp).format("YYYY-MM-DD HH:mm:ss") : '';
    };
    
  return (
    <>
      <Modal
        title={t('taskManagement.list.feedbackModal.title')}
        open={visible}
        onCancel={onClose}
        width={1000}
        footer={
          <Button onClick={onClose}>
            {t('common.close')}
          </Button>
        }
      >
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={feedbackList}
            rowKey={(record) => record.itemCode}
            pagination={false}
            scroll={{ y: 400 }}
          />
        </Spin>
      </Modal>

      {/* 反馈详情抽屉 */}
      {/* <Drawer
        title={t('taskManagement.list.feedbackModal.detailTitle')}
        width={600}
        open={drawerVisible}
        onClose={closeDrawer}
        footer={
          <Button onClick={closeDrawer}>
            {t('common.close')}
          </Button>
        }
      >
        {selectedFeedback && (
          <div>
            <Card title={t('taskManagement.list.feedbackModal.basicInfo')} size="small">
              <Descriptions column={2} size="small">
                <Descriptions.Item label={t('taskManagement.list.columns.taskCode')}>
                  {selectedFeedback.orderCode}
                </Descriptions.Item>
                <Descriptions.Item label={t('taskManagement.list.columns.shop')}>
                  {selectedFeedback.warehouseCode}
                </Descriptions.Item>
                <Descriptions.Item label={t('taskManagement.search.displayGroup')}>
                  {selectedFeedback.displayGroupName}
                </Descriptions.Item>
                <Descriptions.Item label={t('taskManagement.search.division')}>
                  {selectedFeedback.divisionName}
                </Descriptions.Item>
                <Descriptions.Item label={t('taskManagement.list.feedbackModal.worker')}>
                  {selectedFeedback.workerName} ({selectedFeedback.workerAccount})
                </Descriptions.Item>
                <Descriptions.Item label={t('taskManagement.list.feedbackModal.fulfillType')}>
                  <Tag color={selectedFeedback.fulfillType === 'Complete' ? 'green' : 'orange'}>
                    {selectedFeedback.fulfillType === 'Complete' 
                      ? t('taskManagement.list.feedbackModal.fulfillTypeComplete')
                      : t('taskManagement.list.feedbackModal.fulfillTypePartial')}
                  </Tag>
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <Card 
              title={t('taskManagement.list.feedbackModal.feedbackInfo')} 
              size="small" 
              style={{ marginTop: 16 }}
            >
              <Descriptions column={1} size="small">
                <Descriptions.Item label={t('taskManagement.list.feedbackModal.reason')}>
                  {selectedFeedback.reason}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <Card 
              title={t('taskManagement.list.feedbackModal.feedbackImages')} 
              size="small" 
              style={{ marginTop: 16 }}
            >
              {selectedFeedback.feedbackImagesList && selectedFeedback.feedbackImagesList.length > 0 ? (
                <List
                  grid={{ gutter: 16, column: 2 }}
                  dataSource={selectedFeedback.feedbackImagesList}
                  renderItem={(image, index) => (
                    <List.Item>
                      <Image
                        src={image}
                        alt={`Feedback Image ${index + 1}`}
                        style={{ width: '100%', height: 200, objectFit: 'cover' }}
                      />
                    </List.Item>
                  )}
                />
              ) : (
                <p>{t('taskManagement.list.feedbackModal.noImages')}</p>
              )}
            </Card>
          </div>
        )}
      </Drawer> */}
    </>
  );
};

export default FeedbackModal;